import dynamic from 'next/dynamic';
import type { ReactElement } from 'react';
import React from 'react';

import CustomMetatag from '@/components/layouts/custom-metatag';
import Layout from '@/components/layouts/lcv';
import Banner, { IServiceBanner } from '@/components/sections/service/banner';
import FAQ, { ServiceFaqProps } from '@/components/sections/service/faq';
import Intro, { IServiceIntro } from '@/components/sections/service/intro';
import {
  RecommendedParts,
  RecommendedPartsProps,
} from '@/components/sections/service/part-detail/recommended-parts';
import {
  BUTTON_LCV_FIND_SERVICE_CENTER,
  MENU_LCV_SERVICE_MAINTENANCE_LIST,
  MENU_LCV_SERVICE_PARTS,
} from '@/contents/lcv/link';
import { PART_PAGE_METATAG } from '@/contents/lcv/parts';
import { getPartsByConfig } from '@/services/contentstack';

const FooterLCVGroup = dynamic(
  () => import('@/components/layouts/lcv/footer-group'),
  {
    ssr: false,
  }
);
export async function getStaticProps() {
  const resParts = await getPartsByConfig('lcv');
  const partsEntries = resParts?.data?.entries?.map((group) => {
    const category = group.parts_category?.[0] || {};
    const items = group.parts_item || [];

    return {
      title: category.title ?? '',
      url: category.url ?? '',
      items:
        items?.map((item) => ({
          index: item.index ?? 0,
          uid: item.uid ?? '',
          title: item.headline_title ?? '',
          vehicleType: item.vehicle_type ?? '',
          url: item.url ?? '',
          category: item.category?.[0]
            ? {
                uid: item.category[0]?.uid ?? '',
                name: item.category[0]?.name ?? '',
                url: item.category[0]?.url ?? '',
              }
            : null,
          itemImage: {
            url: item.item_image?.url ?? '',
          },
          genuineParts:
            item.genuine_parts?.map((part) => ({
              isStartPrice: part?.is_start_price ?? false,
              isIsuzuGenuinePart: part?.is_isuzu_genuine_part ?? false,
              isTripetchGenuinePart: part?.is_tripetch_genuine_part ?? false,
              isBvpPart: part?.is_bvp_part ?? false,
              title: part?.title ?? '',
              price: part?.price ?? null,
              specialPrice: part?.special_price ?? null,
              unit: part?.unit ?? '',
            })) ?? [],
        })) ?? [],
    };
  });

  return {
    props: { partsEntries },
  };
}

const Page = ({ partsEntries }) => {
  const BANNER_DATA: IServiceBanner = {
    dataTestSection: 'section_banner',
    dataTrack: 'parts-banner-cta',
    title: 'อะไหล่สำหรับรถอีซูซุ',
    description:
      'อะไหล่แท้คุณภาพสูง <span>ทุกชิ้นรับประกัน 6 เดือน หรือ 10,000 กม.</span>',
    buttons: [BUTTON_LCV_FIND_SERVICE_CENTER],
    // imageMobile: {
    //   src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltf085f7d67228e5fd/62aaf7c634f22956975fc6d1/banner-mobile.jpg',
    //   alt: 'Parts',
    //   width: 600,
    //   height: 400,
    // },
    // imageDesktop: {
    //   src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltb0bc0e89ad9b3f5b/62aaf7c7c949fd5059e8b24f/banner-desktop.jpg',
    //   alt: 'Parts',
    //   width: 1200,
    //   height: 450,
    // },
  };

  const INTRO_DATA: IServiceIntro = {
    dataTestSection: 'section_intro',
    dataTrack: 'parts-intro-cta',
    youtubeId: 'EBt0p6k0SN4',
    bgColor: 'light',
    image: {
      src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt33f97025e574b53a/61c58ff94376c54ccd7d0040/bg.jpg',
      alt: 'Isuzu Parts',
      width: 600,
      height: 450,
      priority: true,
    },
    lead: 'คุณภาพที่คุณมั่นใจ',
    title: 'อะไหล่สำหรับรถอีซูซุ',
    description: `<p><strong>อะไหล่แท้อีซูซุ</strong><br/>คุณภาพเดียวกับอะไหล่ที่ติดตั้งมากับตัวรถ ทดสอบและรับรองโดย บริษัท อีซูซุมอเตอร์ จำกัด (ประเทศญี่ปุ่น)</p>
    <p><strong>อะไหล่แท้ทางเลือก</strong></p>
    <ul>
    <li><strong>อีซูซุเบสท์แวรลู </strong>อะไหล่ทางเลือกราคาย่อมเยา ทดสอบและรับรองคุณภาพโดยบริษัท อีซูซุมอเตอร์ จำกัด (ประเทศญี่ปุ่น)</li>
    <li><strong>อะไหล่แท้ตรีเพชร </strong>อะไหล่ทางเลือกราคาย่อมเยา ทดสอบและรับรองคุณภาพโดยบริษัท ตรีเพชรอีซูซุเซลส์ จำกัด ครอบคลุมงานซ่อมบำรุงกว่า 1,000 รายการ</li>
    </ul>`,
    logo: {
      src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt93408f7ad9542cde/65e8200e01f38e8b4012be18/logo-parts-warranty.svg',
      alt: 'logo parts warranty',
      width: 214,
      height: 74,
      priority: true,
    },
    logoFakePart: {
      src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt64b92ff9d11f16bf/65e81ef51ab2dc53fb979e86/logo-fake-part.svg',
      alt: 'logo fake part',
      width: 214,
      height: 74,
      priority: true,
    },
    remark:
      'ทุกชิ้น รับประกัน 6 เดือน หรือ 10,000 กม. (แล้วแต่อย่างใดอย่างหนึ่งจะถึงก่อน)',
  };

  const PARTS_DATA: RecommendedPartsProps = {
    cardUrl: MENU_LCV_SERVICE_PARTS.url,
    groups: partsEntries,
    lead: 'Recommended Parts',
    title: 'ผลิตภัณฑ์แนะนำ',
    description:
      'แนะนำอะไหล่แท้ คุณภาพสูง คัดสรร และทดสอบมาเพื่อรถอีซูซุโดยเฉพาะ ครอบคลุมงานบำรุงรักษาตามระยะ และงานซ่อม มีให้เลือกทั้ง อะไหล่แท้อีซูซุ และอะไหล่แท้ตรีเพชร เพื่อให้เหมาะสมกับความต้องการของลูกค้า ',
    buttons: [
      {
        label: MENU_LCV_SERVICE_MAINTENANCE_LIST.label,
        url: MENU_LCV_SERVICE_MAINTENANCE_LIST.url,
        target: MENU_LCV_SERVICE_MAINTENANCE_LIST.target ?? undefined,
      },
      {
        label: 'รายการอะไหล่แท้ตรีเพชรทั้งหมด',
        url: 'https://assets.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt8fc953adc5cab748/lcv-parts-catalog',
        target: '_blank',
        icon: 'file-download',
        type: 'border',
      },
    ],
    dataTrack: 'recommended-parts',
    dataTestSection: 'section_recommendedParts',
    dataTest: 'recommendedParts',
  };

  const TERMS_AND_CONDITIONS_DATA: ServiceFaqProps = {
    dataTestSection: 'section_condition',
    dataTrack: 'parts-condition-cta',
    isSmall: true,
    theme: 'gray',
    title: 'ข้อกำหนดและเงื่อนไข',
    items: [
      {
        title: 'หมายเหตุ',
        description: `<ul>
          <li>สอบถามรายละเอียดและเงื่อนไขได้ที่ ศูนย์บริการมาตรฐานอีซูซุ</li>
          <li>เงื่อนไขเป็นไปตามที่บริษัทฯ กำหนด</li>
        </ul>`,
      },
    ],
  };

  return (
    <>
      <CustomMetatag
        title={PART_PAGE_METATAG?.title ?? ''}
        description={PART_PAGE_METATAG?.description ?? ''}
        imgURL={PART_PAGE_METATAG?.img?.url ?? ''}
      />
      <main>
        <Banner {...BANNER_DATA} />
        <Intro {...INTRO_DATA} />
        <RecommendedParts {...PARTS_DATA} />
        <FAQ {...TERMS_AND_CONDITIONS_DATA} />
      </main>
      <FooterLCVGroup />
    </>
  );
};

Page.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};

export default Page;
